// src/lib/notificationRouting.js
// Helpers to resolve deep-link routes for notifications

export function buildUrl(to, params = {}) {
  if (!to) return "";
  try {
    const url = new URL(to, window.location.origin);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value == null || value === "") return;
      url.searchParams.set(key, String(value));
    });
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    if (!params || Object.keys(params).length === 0) return to;
    const qs = new URLSearchParams(params);
    return `${to}${to.includes("?") ? "&" : "?"}${qs.toString()}`;
  }
}

const OPEN_PARAM_MAP = {
  task: "eventId",
  planner_task: "eventId",
  offer: "offerId",
  booking: "bookingId",
  kiln_firing: "firingId",
  kiln_run: "kilnRunId",
  thread: "threadId",
};

function includesKeyword(value, keywords = []) {
  if (!value) return false;
  const normalized = String(value).toLowerCase();
  return keywords.some((keyword) => keyword && normalized.includes(keyword));
}

function resolveOpenParams(action) {
  const open = action?.open;
  if (!open?.type || !open?.id) return {};
  const key = OPEN_PARAM_MAP[String(open.type).toLowerCase()];
  if (!key) return {};
  return { [key]: open.id };
}

export function getNotificationRoute(n = {}) {
  const action = n.action || null;
  if (action?.to) {
    return buildUrl(action.to, { ...action.params, ...resolveOpenParams(action) });
  }

  const route = n.route || n.target?.route;
  if (route) return route;

  const world = (n.world || "any").toLowerCase();
  const type = (n.type || n.source || "").toLowerCase();
  const entityType = (n.entityType || n.entity?.type || n.entity_type || n.target?.entity || "").toLowerCase();
  const entityId = n.entityId || n.entity?.id || n.entity_id || n.target?.entityId || n.meta?.entityId || null;

  const idParam = entityId ? encodeURIComponent(entityId) : null;

  const bookingKeywords = ["booking", "booked", "workshop", "space"];
  const shopKeywords = ["offer", "order", "purchase"];
  const isMessageType =
    includesKeyword(type, ["message"]) ||
    includesKeyword(entityType, ["message", "thread"]);
  const isBookingNotification =
    includesKeyword(type, bookingKeywords) || includesKeyword(entityType, bookingKeywords);
  const isShopNotification =
    !isMessageType &&
    (type === "shop" ||
      includesKeyword(type, shopKeywords) ||
      includesKeyword(entityType, shopKeywords) ||
      entityType === "shop");
  const bookingRoot = world === "visitor" ? "/discover/workshops" : "/dashboard/workshops";

  // Bookings
  if (isBookingNotification) {
    return idParam ? `${bookingRoot}?bookingId=${idParam}` : bookingRoot;
  }

  // Offers
  if (isShopNotification || type.startsWith("offer") || entityType === "offer") {
    if (world === "studio") {
      return idParam
        ? `/dashboard/shop?tab=offers&offerId=${idParam}`
        : "/dashboard/shop?tab=offers";
    }
    // visitor or any
    return idParam
      ? `/discover/marketplace?tab=my-offers&offerId=${idParam}`
      : "/discover/marketplace?tab=my-offers";
  }

  // Tasks / Planner
  if (type.startsWith("task") || entityType === "task" || type === "planner") {
    return idParam ? `/dashboard/planner?eventId=${idParam}` : "/dashboard/planner";
  }

  // Kiln
  if (type.startsWith("kiln") || entityType === "kiln" || entityType === "firing") {
    return idParam
      ? `/dashboard/kiln-logs?firingId=${idParam}`
      : "/dashboard/kiln-logs";
  }

  // Messages – default to unified inbox locations
  if (type.includes("message") || entityType === "message" || entityType === "thread") {
    if (world === "studio") {
      return idParam
        ? `/dashboard/shop?threadId=${idParam}&openInbox=1`
        : "/dashboard/shop?openInbox=1";
    }
    return idParam
      ? `/discover/marketplace?threadId=${idParam}&openInbox=1`
      : "/discover/marketplace?openInbox=1";
  }

  // Fallback by world
  if (world === "studio") return "/dashboard";
  if (world === "visitor") return "/discover";
  return "/";
}

export function navigateFromNotification(notification, navigate) {
  const to = getNotificationRoute(notification);
  if (to && typeof navigate === "function") {
    navigate(to);
    return { ok: true, to };
  }
  return { ok: false, reason: "missing_route" };
}

export function navigateForNotification(n, navigate) {
  return navigateFromNotification(n, navigate);
}
